import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { initLeadAttribution } from '../utils/leadTracking';
import {
  POSTHOG_KEY,
  POSTHOG_HOST,
  GA4_MEASUREMENT_ID,
  META_PIXEL_ID,
} from '../config/analyticsConfig';

function loadScript(src, id) {
  if (id && document.getElementById(id)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  if (id) s.id = id;
  document.head.appendChild(s);
}

/**
 * PostHog (public key / env), optional GA4 + Meta Pixel via env.
 */
export default function Analytics() {
  const location = useLocation();
  const phInit = useRef(false);

  useEffect(() => {
    initLeadAttribution();
  }, []);

  useEffect(() => {
    if (!POSTHOG_KEY || phInit.current) return;
    phInit.current = true;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,
    });
  }, []);

  useEffect(() => {
    if (POSTHOG_KEY && posthog.capture) {
      posthog.capture('$pageview', { path: location.pathname + location.search });
    }
  }, [location.pathname, location.search]);

  /** Single GA4 path: load gtag once, then config per route (avoids double page_view on first paint). */
  useEffect(() => {
    if (!GA4_MEASUREMENT_ID) return;
    const path = location.pathname + location.search;
    const sendPage = () => {
      if (!window.gtag) return;
      window.gtag('config', GA4_MEASUREMENT_ID, {
        page_path: path,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined,
      });
    };

    if (document.getElementById('ga4-base')) {
      sendPage();
      return;
    }

    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    s1.id = 'ga4-base';
    s1.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      sendPage();
    };
    document.head.appendChild(s1);
  }, [location.pathname, location.search]);

  const metaSpaPv = useRef(false);

  useEffect(() => {
    if (!META_PIXEL_ID) return;
    if (window._fbq) return;
    const n = (window.fbq =
      window.fbq ||
      function fbqStub() {
        window.fbq.callMethod
          ? window.fbq.callMethod.apply(window.fbq, arguments)
          : window.fbq.queue.push(arguments);
      });
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    loadScript('https://connect.facebook.net/en_US/fbevents.js', 'fb-pixel');
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }, []);

  useEffect(() => {
    if (!META_PIXEL_ID || !window.fbq) return;
    if (!metaSpaPv.current) {
      metaSpaPv.current = true;
      return;
    }
    window.fbq('track', 'PageView');
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Funnel + diagnostics. On successful lead submit, also fires GA4 `generate_lead` and Meta standard `Lead`.
 */
export function trackEvent(name, props) {
  try {
    if (POSTHOG_KEY && posthog.capture) posthog.capture(name, props || {});

    if (window.gtag && GA4_MEASUREMENT_ID) {
      window.gtag('event', name, props || {});
      if (props?.ok === true) {
        if (name === 'contact_form_submit') {
          window.gtag('event', 'generate_lead', {
            lead_source: 'website_contact',
            form_id: 'contact_section',
          });
        }
        if (name === 'configurator_submit') {
          window.gtag('event', 'generate_lead', {
            lead_source: 'project_builder',
            form_id: 'estimate_page',
          });
        }
      }
    }

    if (window.fbq) {
      if ((name === 'contact_form_submit' || name === 'configurator_submit') && props?.ok === true) {
        const content_name = name === 'contact_form_submit' ? 'website_contact' : 'project_builder';
        const content_category = name === 'contact_form_submit' ? 'contact_form' : 'estimate';
        window.fbq('track', 'Lead', { content_name, content_category });
      } else {
        window.fbq('trackCustom', name, props || {});
      }
    }
  } catch {
    /* ignore */
  }
}
